import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { useExperiment } from '@fs/zion-experiments'
import { i18n } from '@fs/zion-locale'
import { getEncodedArk, getSearchedImageName, getSearchedImageNamesByDgs } from 'helpers'
import { getImageApidByName } from 'components/FormInputs/queries'
import useGroupSearch from './useGroupSearch'

export default function useImageDetailsUrl(apidData) {
  const { groupSearchQueryParams } = useGroupSearch()
  const { imageApid, imageArk, imageGroupNumbers } = groupSearchQueryParams
  const client = useApolloClient()
  const searchedImageName = getSearchedImageName(imageGroupNumbers)
  const imageNamesByDgs = useMemo(() => getSearchedImageNamesByDgs(imageGroupNumbers), [imageGroupNumbers])
  const imageNamesToLookUp = imageApid || imageArk ? '' : Object.values(imageNamesByDgs).join(',')
  const [apidsByImageName, setApidsByImageName] = useState({})

  // Look up every searched image name in parallel, since a useLazyQuery would abort all but the last
  useEffect(() => {
    let isCurrent = true
    setApidsByImageName({})
    if (!imageNamesToLookUp) return undefined

    Promise.all(
      imageNamesToLookUp.split(',').map((imageName) =>
        client
          .query({ query: getImageApidByName, variables: { imageName } })
          .then(({ data }) => [imageName, data?.imageApidByName])
          .catch((err) => {
            console.error(`error fetching apid for image name ${imageName}`, err)
            return [imageName, undefined]
          })
      )
    ).then((entries) => {
      if (isCurrent) setApidsByImageName(Object.fromEntries(entries))
    })
    return () => {
      isCurrent = false
    }
  }, [client, imageNamesToLookUp])
  const [imageDetailsUrlEx] = useExperiment('imageDetailsUrlEx')

  const getReactImageDetailsUrl = useCallback(
    (group) => {
      const id = group?.id
      const imageApids = group?.imageApids || []
      const imageApidsToUse = [...imageApids]
      // A lone image name search finds just its group; among several, each group opens to its lowest numbered searched image
      const dgs = group?.metadata?.imageGroupNumber?.replace(/^0+/, '')
      const imageName = searchedImageName ?? imageNamesByDgs[dgs]
      // Insert the searched for image apid into the start of the array
      const apid = imageApid ?? apidData?.imageApid ?? apidsByImageName[imageName]
      if (apid) {
        imageApidsToUse.unshift(apid)
      }
      const encodedArk = imageArk || getEncodedArk({ id, imageApids: imageApidsToUse })
      return `${window.location.origin}/ark:/61903/${encodedArk}?view=explore&groupId=${id}&grid=on`
    },
    [apidData, apidsByImageName, imageApid, imageArk, imageNamesByDgs, searchedImageName]
  )

  const getImageDetailsUrl = useCallback(
    (group) => {
      const searchParams = new URLSearchParams(groupSearchQueryParams)
      const groupUrl =
        imageDetailsUrlEx || i18n.language !== 'zh'
          ? getReactImageDetailsUrl(group)
          : `${window.location.origin}/records/images/image-details?rmsId=${group.id}&${searchParams.toString()}`
      return groupUrl
    },
    [getReactImageDetailsUrl, groupSearchQueryParams, imageDetailsUrlEx]
  )

  return getImageDetailsUrl
}
